import React, { useState, useMemo, useEffect } from 'react';
import {
    ChevronRight,
    Search
} from 'lucide-react';
import { JURISDICTION_GROUPS } from '../../utils/jurisdictionData';
import { motion } from 'framer-motion';
import FormulaReportModal from './FormulaReportModal';
import { API_BASE_URL as API_BASE } from '../../config/apiConfig';

const AlgorithmicWorkspace = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedCounty, setSelectedCounty] = useState('All');
    const [isReportOpen, setIsReportOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [cityFormulas, setCityFormulas] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const itemsPerPage = 8;

    // Fetch real formulae from backend
    useEffect(() => {
        const fetchFormulae = async () => {
            try {
                const response = await fetch(`${API_BASE}/admin/formulae`);
                const data = await response.json();

                if (Array.isArray(data)) {
                    // Filter out LAH to match 63-city requirement
                    const filteredData = data.filter(c => c.city_code !== 'LAH');
                    // Enrich with county info from JURISDICTION_GROUPS
                    const enrichedData = filteredData.map(city => {
                        let countyInfo = { county: 'Other', color: 'slate' };

                        for (const group of JURISDICTION_GROUPS) {
                            if (group.cities.some(c => c.toUpperCase() === city.name.toUpperCase())) {
                                countyInfo = {
                                    county: group.county,
                                    color: group.color.replace('bg-', '')
                                };
                                break;
                            }
                        }

                        return {
                            ...city,
                            county: countyInfo.county,
                            countyColor: countyInfo.color,
                            cityKey: city.city_code.toLowerCase(),
                            disclaimer: "Verification Required: Jurisdictional data is for administrative reference only. Always cross-reference with official municipal code."
                        };
                    });
                    setCityFormulas(enrichedData);
                }
            } catch (error) {
                console.error("Failed to fetch formulae:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchFormulae();
    }, []);

    const searchedCities = useMemo(() => {
        const primaryCounties = ['Santa Clara', 'Alameda', 'San Mateo', 'Contra Costa', 'Marin', 'Napa', 'Solano', 'Calaveras'];
        return cityFormulas.filter(city => {
            const matchesSearch = city.name.toLowerCase().includes(searchTerm.toLowerCase());
            const isPrimary = primaryCounties.includes(city.county);
            return matchesSearch && isPrimary;
        });
    }, [cityFormulas, searchTerm]);

    const filteredCities = useMemo(() => {
        return searchedCities.filter(city =>
            selectedCounty === 'All' || city.county === selectedCounty
        ).sort((a, b) => a.name.localeCompare(b.name));
    }, [searchedCities, selectedCounty]);


    // Set initial city and handle pagination reset
    useEffect(() => {
        if (!selectedCity && filteredCities.length > 0) {
            setSelectedCity(filteredCities[0]);
        }
    }, [filteredCities, selectedCity]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedCounty]);

    const totalPages = Math.ceil(filteredCities.length / itemsPerPage);
    const paginatedCities = filteredCities.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const counties = ['All', 'Santa Clara', 'Alameda', 'San Mateo', 'Contra Costa', 'Marin', 'Napa', 'Solano', 'Calaveras'];

    return (
        <div className="flex flex-col gap-8 animate-in fade-in slide-in-from-bottom-4 duration-700">

            {/* Main Context Header */}
            <div className="flex flex-col gap-2 pl-4">
                <div className="flex items-center space-x-3 mb-1">
                    <div className="w-10 h-1 bg-indigo-600 rounded-full"></div>
                    <span className="text-[11px] font-black text-slate-400 uppercase tracking-[0.4em]">Integrated Intelligence</span>
                </div>
                <h1 className="text-5xl font-black text-slate-900 tracking-tighter leading-none flex items-center gap-4">
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-500">Logic <span className="text-indigo-600">Lab</span></span>
                    <div className="h-2 w-2 rounded-full bg-indigo-500 mt-2 animate-pulse" />
                </h1>
                <p className="text-slate-500 font-bold text-sm mt-1 max-w-xl leading-relaxed">
                    Jurisdictional Intelligence Protocol / <span className="text-indigo-600 font-black">{filteredCities.length} Nodes Verified</span>
                </p>
            </div>

            {/* Unified Archive Bar (Search + Counties) */}
            <div className="bg-white/80 backdrop-blur-2xl border border-slate-200/60 p-2 rounded-[1.5rem] shadow-2xl shadow-slate-200/40 flex flex-col md:flex-row items-center gap-4">
                {/* Integrated Search with Match Badge */}
                <div className="relative group flex-1 w-full">
                    <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 group-focus-within:text-indigo-500 transition-colors z-20" />
                    <input
                        type="text"
                        placeholder="Find anything..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-14 pr-32 py-4 bg-transparent text-sm font-semibold focus:outline-none placeholder:text-slate-300 text-slate-900 z-10"
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2 bg-slate-900 px-4 py-2.5 rounded-xl border border-slate-800 shadow-lg">
                        <span className="text-[10px] font-black text-white tabular-nums">{filteredCities.length}</span>
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-tighter">Matches</span>
                    </div>
                </div>

                <div className="hidden md:block w-px h-10 bg-slate-200/60 mx-2" />

                {/* Minimalist County Selector */}
                <div className="flex items-center gap-2 p-1 flex-shrink-0">
                    {counties.map((c) => {
                        const isActive = selectedCounty === c;

                        return (
                            <button
                                key={c}
                                onClick={() => setSelectedCounty(c)}
                                className={`relative px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-[0.15em] transition-all group overflow-hidden ${isActive ? "text-slate-900 bg-slate-50" : "text-slate-400 hover:text-slate-600 hover:bg-slate-50/50"}`}
                            >
                                <span className="relative z-10">{c}</span>
                                {isActive && (
                                    <motion.div
                                        layoutId="activeZenDot"
                                        className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.6)]"
                                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Dedicated Table Card */}
            <div className="bg-white rounded-[2.5rem] border border-slate-200/60 shadow-2xl shadow-slate-200/40 overflow-hidden flex flex-col h-[calc(100vh-380px)]">
                <div className="flex-1 overflow-x-auto overflow-y-auto no-scrollbar bg-white">
                    <table className="w-full border-collapse min-w-[1000px]">
                        <thead className="sticky top-0 z-40">
                            <tr className="bg-gradient-to-r from-blue-700 to-indigo-800 border-b border-white/10">
                                <th className="px-10 py-5 text-left text-[11px] font-black text-white uppercase tracking-[0.25em]">Jurisdiction Name</th>
                                <th className="px-6 py-5 text-left text-[11px] font-black text-white uppercase tracking-[0.25em]">County Region</th>
                                <th className="px-6 py-5 text-center text-[10px] font-black text-white uppercase tracking-[0.2em] bg-white/5">Metrics Status</th>
                                <th className="px-10 py-5 text-right text-[11px] font-black text-white uppercase tracking-[0.25em]">Actions</th>
                            </tr>
                        </thead>

                        {isLoading ? (
                            <tbody>
                                <tr>
                                    <td colSpan="4" className="py-20 text-center">
                                        <div className="flex flex-col items-center justify-center gap-4">
                                            <motion.div
                                                animate={{ rotate: 360 }}
                                                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                                                className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full"
                                            />
                                            <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest animate-pulse">Syncing Logic Nodes...</p>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <tbody className="divide-y divide-slate-100/50">
                                {paginatedCities.map((city, idx) => {
                                    const isLosGatos = city.name && city.name.toUpperCase().includes("LOS GATOS");
                                    const metrics = [
                                        { id: 'slope', label: 'Slope' },
                                        { id: 'luf', label: 'LUF' },
                                        { id: 'mfa', label: isLosGatos ? 'FAR' : 'MFA' },
                                        { id: 'mda', label: 'MDA', hide: isLosGatos }
                                    ].filter(m => !m.hide).map(m => ({
                                        ...m,
                                        exists: city.profiles && (city.profiles[m.id] || city.profiles[m.id.toUpperCase()])
                                    }));

                                    return (
                                        <motion.tr
                                            key={city.cityKey}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: idx * 0.03 }}
                                            className="group transition-all cursor-default relative"
                                        >
                                            <td className="px-10 py-6 relative z-10">
                                                <div className="flex items-center gap-4">
                                                    <div className={`w-3 h-3 rounded-full bg-${city.countyColor}-500 shadow-lg shadow-${city.countyColor}-200 animate-pulse`} />
                                                    <span className="text-xs font-black text-slate-900 uppercase tracking-tight group-hover:text-indigo-600 transition-colors">{city.name}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-6 relative z-10 text-left">
                                                <span className={`px-4 py-1.5 bg-${city.countyColor}-50 text-${city.countyColor}-600 text-[9px] font-black uppercase tracking-widest rounded-full border border-${city.countyColor}-100`}>
                                                    {city.county}
                                                </span>
                                            </td>
                                            <td className="px-6 py-6 relative z-10">
                                                <div className="flex items-center justify-center gap-2">
                                                    {metrics.map(m => (
                                                        <div
                                                            key={m.id}
                                                            title={`${m.label}: ${m.exists ? 'Verified' : 'Not Defined'}`}
                                                            className={`w-8 h-8 rounded-lg flex items-center justify-center border transition-all ${m.exists
                                                                ? 'bg-emerald-50 border-emerald-100 text-emerald-600 shadow-sm'
                                                                : 'bg-slate-50 border-slate-100 text-slate-200 opacity-40'
                                                                }`}
                                                        >
                                                            <span className="text-[8px] font-black">{m.label}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="px-10 py-6 relative z-10 text-right">
                                                <button
                                                    onClick={() => {
                                                        setSelectedCity(city);
                                                        setIsReportOpen(true);
                                                    }}
                                                    className="px-6 py-3 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-600 hover:scale-105 active:scale-95 transition-all shadow-lg shadow-slate-200 flex items-center gap-2 ml-auto w-fit"
                                                >
                                                    View Report
                                                    <ChevronRight className="w-3 h-3" />
                                                </button>
                                            </td>
                                            {/* Hover Backdrop Overlay */}
                                            <td className="absolute inset-x-2 inset-y-1.5 bg-slate-50/80 backdrop-blur-sm rounded-2xl border border-slate-200/50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-0" colSpan="4" />
                                        </motion.tr>
                                    );
                                })}
                                {filteredCities.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="py-20 text-center text-sm font-bold text-slate-400 uppercase tracking-widest">
                                            No jurisdictions found matching your search
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        )}
                    </table>
                </div>

                {/* Bottom Status Bar */}
                <div className="px-10 py-8 border-t border-slate-100 bg-[#FBFBFC]/80 backdrop-blur-xl flex items-center justify-between">
                    <div className="flex items-center gap-8">
                        <div className="flex flex-col">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Architecture Core</span>
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-black text-slate-900 tracking-tighter uppercase">SYSTEM_ANALYZER_v6</span>
                                <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></div>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-xl border border-slate-200/40">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                className="p-2 rounded-lg text-slate-400 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronRight className="w-4 h-4 rotate-180" />
                            </button>
                            <div className="flex items-center gap-1 px-4">
                                <span className="text-xs font-black text-slate-900">{currentPage}</span>
                                <span className="text-xs font-bold text-slate-300">/</span>
                                <span className="text-xs font-bold text-slate-400">{totalPages || 1}</span>
                            </div>
                            <button
                                disabled={currentPage >= totalPages}
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                className="p-2 rounded-lg text-slate-400 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="w-px h-8 bg-slate-100" />
                        <div className="flex flex-col items-end">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Logic Status</span>
                            <span className="text-xs font-bold text-slate-900">7-County Verified</span>
                        </div>
                    </div>
                </div>
            </div>
            <FormulaReportModal
                isOpen={isReportOpen}
                onClose={() => setIsReportOpen(false)}
                city={selectedCity}
            />
        </div>
    );
};

export default AlgorithmicWorkspace;
